import React, { useEffect, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Skeleton } from "@/components/ui/skeleton";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { Star, Link as LinkIcon, Users2, Pencil, Check, X, Loader2, User, Settings } from "lucide-react";
import ChallengeBriefSections from "./ChallengeBriefSections";
import TeamMembersList from "./TeamMembersList";
import ChallengeUpdatesTimeline from "./ChallengeUpdatesTimeline";
import ClaimChallengeFlow from "./ClaimChallengeFlow";
import JoinTeamButton from "./JoinTeamButton";
import ChallengeStatusManager from "./ChallengeStatusManager";
import StartTeamInviteFlow from "./StartTeamInviteFlow";
import ChallengeClaimManager from "./ChallengeClaimManager";
import TaskList from "./tasks/TaskList";
import type { Challenge } from "./ChallengeBoard";

interface Event {
  id: string;
  name: string;
  event_date: string;
}

interface ChallengeModalProps {
  challengeId: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onUpdate?: () => void;
  initialEditMode?: boolean;
}

const ChallengeModal: React.FC<ChallengeModalProps> = ({
  challengeId,
  open,
  onOpenChange,
  onUpdate,
  initialEditMode = false,
}) => {
  const [challenge, setChallenge] = useState<(Challenge & { claimed_by?: string | null }) | null>(null);
  const [loading, setLoading] = useState(true);
  const [currentUserId, setCurrentUserId] = useState<string | null>(null);
  const [isTeamMember, setIsTeamMember] = useState(false);
  const [isAnyTeamMember, setIsAnyTeamMember] = useState(false);
  const [isTeamLead, setIsTeamLead] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  const [isClaimant, setIsClaimant] = useState(false);
  const [claimantEmail, setClaimantEmail] = useState<string | null>(null);
  const [events, setEvents] = useState<Event[]>([]);
  const [updatingFeatured, setUpdatingFeatured] = useState(false);
  const [teamInfo, setTeamInfo] = useState<{ id: string; name: string } | null>(null);
  const [showStartTeamInvite, setShowStartTeamInvite] = useState(false);
  
  // Title editing state
  const [isEditingTitle, setIsEditingTitle] = useState(false);
  const [editedTitle, setEditedTitle] = useState("");
  const [savingTitle, setSavingTitle] = useState(false);
  
  // Description editing state
  const [isEditingDescription, setIsEditingDescription] = useState(false);
  const [editedDescription, setEditedDescription] = useState("");
  const [savingDescription, setSavingDescription] = useState(false);
  
  // Tags editing state
  const [isEditingTags, setIsEditingTags] = useState(false);
  const [editedTags, setEditedTags] = useState("");
  const [savingTags, setSavingTags] = useState(false);
  
  // Edit mode toggle
  const [editModeActive, setEditModeActive] = useState(initialEditMode);

  // Derived permission
  const canEdit = isAdmin || isClaimant;

  useEffect(() => {
    if (open && challengeId) {
      fetchChallenge();
      checkUserStatus();
    }
  }, [open, challengeId]);

  const fetchChallenge = async () => {
    try {
      const { data, error } = await supabase
        .from("challenges")
        .select("*")
        .eq("id", challengeId)
        .single();

      if (error) throw error;
      setChallenge(data);
      
      // Fetch claimant email if claimed
      if (data?.claimed_by) {
        const { data: profile } = await supabase
          .from("profiles")
          .select("email")
          .eq("id", data.claimed_by)
          .single();
        setClaimantEmail(profile?.email || null);
      } else {
        setClaimantEmail(null);
      }
    } catch (error) {
      console.error("Error fetching challenge:", error);
      toast.error("Failed to load challenge details");
    } finally {
      setLoading(false);
    }
  };

  const checkUserStatus = async () => {
    const { data: { user } } = await supabase.auth.getUser();
    setCurrentUserId(user?.id || null);

    if (user) {
      // Fetch challenge to check claimant
      const { data: challengeData } = await supabase
        .from("challenges")
        .select("claimed_by")
        .eq("id", challengeId)
        .single();
      
      setIsClaimant(challengeData?.claimed_by === user.id);

      // Check team membership and get team info
      const { data: membership } = await supabase
        .from("challenge_team_members")
        .select(`
          role,
          team_id,
          challenge_teams (
            id,
            name,
            deleted_at
          )
        `)
        .eq("challenge_id", challengeId)
        .eq("user_id", user.id)
        .maybeSingle();

      // Filter out if team is soft-deleted
      const hasActiveTeam = membership?.challenge_teams && 
        !(membership.challenge_teams as any).deleted_at;

      setIsTeamMember(!!hasActiveTeam);
      setIsAnyTeamMember(!!hasActiveTeam);
      setIsTeamLead(hasActiveTeam && membership?.role === "lead");
      
      if (hasActiveTeam) {
        const team = membership.challenge_teams as unknown as { id: string; name: string };
        setTeamInfo({ id: team.id, name: team.name });
      } else {
        setTeamInfo(null);
      }

      // Check admin role
      const { data: roleData } = await supabase
        .from("user_roles")
        .select("role")
        .eq("user_id", user.id)
        .in("role", ["admin", "super_admin"]);

      const hasAdmin = (roleData?.length || 0) > 0;
      setIsAdmin(hasAdmin);

      // Fetch events for admin linking
      if (hasAdmin) {
        const { data: eventsData } = await supabase
          .from("events")
          .select("id, name, event_date")
          .gte("event_date", new Date().toISOString().split("T")[0])
          .order("event_date", { ascending: true });
        
        setEvents(eventsData || []);
      }
    }
  };

  const handleUpdate = () => {
    fetchChallenge();
    checkUserStatus();
    onUpdate?.();
  };

  const handleSaveTitle = async () => {
    if (!challenge || !editedTitle.trim()) return;
    setSavingTitle(true);
    
    try {
      const { error } = await supabase
        .from("challenges")
        .update({ title: editedTitle.trim() })
        .eq("id", challenge.id);
      
      if (error) throw error;
      toast.success("Challenge title updated");
      setIsEditingTitle(false);
      handleUpdate();
    } catch (error) {
      console.error("Error updating title:", error);
      toast.error("Failed to update title");
    } finally {
      setSavingTitle(false);
    }
  };

  const handleSaveDescription = async () => {
    if (!challenge) return;
    setSavingDescription(true);
    
    try {
      const { error } = await supabase
        .from("challenges")
        .update({ one_liner: editedDescription.trim() || null })
        .eq("id", challenge.id);
      
      if (error) throw error;
      toast.success("Description updated");
      setIsEditingDescription(false);
      handleUpdate();
    } catch (error) {
      console.error("Error updating description:", error);
      toast.error("Failed to update description");
    } finally {
      setSavingDescription(false);
    }
  };

  const handleSaveTags = async () => {
    if (!challenge) return;
    setSavingTags(true);
    
    try {
      const newTags = editedTags
        .split(",")
        .map((t) => t.trim())
        .filter(Boolean);
      
      const { error } = await supabase
        .from("challenges")
        .update({ tags: newTags })
        .eq("id", challenge.id);
      
      if (error) throw error;
      toast.success("Tags updated");
      setIsEditingTags(false);
      handleUpdate();
    } catch (error) {
      console.error("Error updating tags:", error);
      toast.error("Failed to update tags");
    } finally {
      setSavingTags(false);
    }
  };

  const handleToggleFeatured = async (featured: boolean) => {
    if (!challenge) return;
    setUpdatingFeatured(true);
    
    try {
      if (featured) {
        await supabase
          .from("challenges")
          .update({ is_featured: false })
          .eq("is_featured", true);
      }

      const { error } = await supabase
        .from("challenges")
        .update({ is_featured: featured })
        .eq("id", challenge.id);

      if (error) throw error;
      toast.success(featured ? "Challenge featured" : "Challenge unfeatured");
      handleUpdate();
    } catch (error) {
      console.error("Error updating featured status:", error);
      toast.error("Failed to update featured status");
    } finally {
      setUpdatingFeatured(false);
    }
  };

  const handleLinkEvent = async (eventId: string | null) => {
    if (!challenge) return;

    try {
      const { error } = await supabase
        .from("challenges")
        .update({ event_id: eventId })
        .eq("id", challenge.id);

      if (error) throw error;
      toast.success(eventId ? "Challenge linked to event" : "Event link removed");
      handleUpdate();
    } catch (error) {
      console.error("Error linking event:", error);
      toast.error("Failed to link event");
    }
  };

  const getDifficultyColor = (difficulty: string) => {
    switch (difficulty) {
      case "beginner":
        return "bg-green-500/20 text-green-600";
      case "intermediate":
        return "bg-yellow-500/20 text-yellow-600";
      case "advanced":
        return "bg-red-500/20 text-red-600";
      default:
        return "bg-muted text-muted-foreground";
    }
  };

  const getStatusLabel = (status: string) => {
    switch (status) {
      case "unclaimed": return "Unclaimed";
      case "in_progress": return "In Progress";
      case "testing": return "Testing";
      case "complete": return "Complete";
      default: return status;
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        {loading ? (
          <div className="space-y-4">
            <Skeleton className="h-8 w-3/4" />
            <Skeleton className="h-4 w-full" />
            <Skeleton className="h-32 w-full" />
          </div>
        ) : challenge ? (
          <>
            <DialogHeader>
              {/* Top row: Status, Difficulty, and Edit Toggle */}
              <div className="flex items-center justify-between gap-2 mb-2">
                <div className="flex flex-wrap gap-2">
                  <Badge variant="outline">
                    {getStatusLabel(challenge.status)}
                  </Badge>
                  <Badge className={getDifficultyColor(challenge.difficulty)}>
                    {challenge.difficulty}
                  </Badge>
                </div>
                
                {/* Prominent Edit Mode Toggle */}
                {canEdit && (
                  <Button
                    variant={editModeActive ? "default" : "outline"}
                    size="sm"
                    onClick={() => setEditModeActive(!editModeActive)}
                    className="gap-1.5 shrink-0"
                  >
                    {editModeActive ? (
                      <>
                        <Check className="h-4 w-4" />
                        Done Editing
                      </>
                    ) : (
                      <>
                        <Settings className="h-4 w-4" />
                        Edit Challenge
                      </>
                    )}
                  </Button>
                )}
              </div>
              
              {/* Tags row - editable when in edit mode */}
              <div className="flex flex-wrap gap-2 mb-2">
                {editModeActive && canEdit && isEditingTags ? (
                  <div className="flex items-center gap-2 w-full">
                    <Input
                      value={editedTags}
                      onChange={(e) => setEditedTags(e.target.value)}
                      placeholder="Tag1, Tag2, Tag3"
                      className="flex-1 text-sm"
                    />
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={handleSaveTags}
                      disabled={savingTags}
                    >
                      {savingTags ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />}
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => setIsEditingTags(false)}
                    >
                      <X className="h-4 w-4" />
                    </Button>
                  </div>
                ) : (
                  <>
                    {challenge.tags?.map((tag) => (
                      <Badge key={tag} variant="secondary">
                        {tag}
                      </Badge>
                    ))}
                    {editModeActive && canEdit && (
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-6 px-2"
                        onClick={() => {
                          setEditedTags(challenge.tags?.join(", ") || "");
                          setIsEditingTags(true);
                        }}
                      >
                        <Pencil className="h-3 w-3 mr-1" />
                        <span className="text-xs">Edit Tags</span>
                      </Button>
                    )}
                  </>
                )}
              </div>
              
              {/* Title - editable when in edit mode */}
              <DialogTitle className="text-xl md:text-2xl">
                {editModeActive && canEdit && isEditingTitle ? (
                  <div className="flex items-center gap-2">
                    <Input
                      value={editedTitle}
                      onChange={(e) => setEditedTitle(e.target.value)}
                      className="text-xl font-semibold h-auto py-1"
                      autoFocus
                      onKeyDown={(e) => {
                        if (e.key === "Enter") handleSaveTitle();
                        if (e.key === "Escape") setIsEditingTitle(false);
                      }}
                    />
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={handleSaveTitle}
                      disabled={savingTitle || !editedTitle.trim()}
                    >
                      {savingTitle ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />}
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => setIsEditingTitle(false)}
                    >
                      <X className="h-4 w-4" />
                    </Button>
                  </div>
                ) : (
                  <div className="flex items-center gap-2">
                    <span>{challenge.title}</span>
                    {editModeActive && canEdit && (
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-7 w-7 p-0"
                        onClick={() => {
                          setEditedTitle(challenge.title);
                          setIsEditingTitle(true);
                        }}
                      >
                        <Pencil className="h-4 w-4" />
                      </Button>
                    )}
                  </div>
                )}
              </DialogTitle>
              
              {/* Description (one_liner) - editable when in edit mode */}
              {editModeActive && canEdit && isEditingDescription ? (
                <div className="flex items-start gap-2 mt-2">
                  <Textarea
                    value={editedDescription}
                    onChange={(e) => setEditedDescription(e.target.value)}
                    className="flex-1"
                    rows={2}
                    autoFocus
                  />
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={handleSaveDescription}
                    disabled={savingDescription}
                  >
                    {savingDescription ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />}
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => setIsEditingDescription(false)}
                  >
                    <X className="h-4 w-4" />
                  </Button>
                </div>
              ) : (
                <div className="flex items-start gap-2 mt-2">
                  <p className="text-muted-foreground flex-1">
                    {challenge.one_liner || (editModeActive && canEdit ? "Add a description..." : "")}
                  </p>
                  {editModeActive && canEdit && (
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-7 w-7 p-0 shrink-0"
                      onClick={() => {
                        setEditedDescription(challenge.one_liner || "");
                        setIsEditingDescription(true);
                      }}
                    >
                      <Pencil className="h-4 w-4" />
                    </Button>
                  )}
                </div>
              )}
              
              {/* Claimant info */}
              {claimantEmail && (
                <div className="flex items-center gap-2 mt-2 text-sm text-muted-foreground">
                  <User className="h-4 w-4" />
                  <span>Claimed by: {claimantEmail}</span>
                </div>
              )}
            </DialogHeader>

            {/* Admin Controls */}
            {isAdmin && (
              <div className="flex flex-col gap-3 py-4 px-4 bg-muted/50 rounded-lg border border-dashed mb-4">
                <div className="flex flex-wrap items-center gap-4">
                  <div className="flex items-center gap-2">
                    <Star className={`h-4 w-4 ${challenge.is_featured ? "fill-primary text-primary" : "text-muted-foreground"}`} />
                    <Label htmlFor="featured-toggle" className="text-sm font-medium">
                      Featured
                    </Label>
                    <Switch
                      id="featured-toggle"
                      checked={challenge.is_featured || false}
                      onCheckedChange={handleToggleFeatured}
                      disabled={updatingFeatured}
                    />
                  </div>
                  
                  <div className="flex items-center gap-2">
                    <LinkIcon className="h-4 w-4 text-muted-foreground" />
                    <Label className="text-sm font-medium">Link to Event</Label>
                    <Select 
                      value={challenge.event_id || "none"} 
                      onValueChange={(val) => handleLinkEvent(val === "none" ? null : val)}
                    >
                      <SelectTrigger className="w-48">
                        <SelectValue placeholder="Select event" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">No event linked</SelectItem>
                        {events.map((event) => (
                          <SelectItem key={event.id} value={event.id}>
                            {event.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                
                {/* Admin Claim Management */}
                <ChallengeClaimManager
                  challengeId={challenge.id}
                  claimedBy={challenge.claimed_by || null}
                  claimantEmail={claimantEmail}
                  onUpdate={handleUpdate}
                />
              </div>
            )}

            {/* Action Buttons */}
            <div className="flex flex-wrap gap-2 py-4 border-y border-border">
              {challenge.status === "unclaimed" && !isTeamMember && !isClaimant && (
                <ClaimChallengeFlow
                  challengeId={challenge.id}
                  onClaimed={handleUpdate}
                />
              )}
              {challenge.status !== "unclaimed" && !isTeamMember && currentUserId && !isClaimant && (
                <JoinTeamButton
                  challengeId={challenge.id}
                  onJoined={handleUpdate}
                />
              )}
              {!isTeamMember && currentUserId && !isClaimant && (
                <Button
                  variant="outline"
                  onClick={() => setShowStartTeamInvite(true)}
                  className="gap-2"
                >
                  <Users2 className="h-4 w-4" />
                  Invite to Start Team
                </Button>
              )}
              {(isTeamLead || isClaimant) && challenge.status !== "complete" && (
                <ChallengeStatusManager
                  challengeId={challenge.id}
                  currentStatus={challenge.status}
                  onStatusChange={handleUpdate}
                />
              )}
            </div>

            {/* Start Team Invite Dialog */}
            <StartTeamInviteFlow
              open={showStartTeamInvite}
              onOpenChange={setShowStartTeamInvite}
              challengeId={challenge.id}
              challengeTitle={challenge.title}
              onSuccess={handleUpdate}
            />

            {/* Tabs */}
            <Tabs defaultValue="brief" className="mt-4">
              <TabsList className="grid w-full grid-cols-4">
                <TabsTrigger value="brief">Brief</TabsTrigger>
                <TabsTrigger value="tasks">Tasks</TabsTrigger>
                <TabsTrigger value="team">Team</TabsTrigger>
                <TabsTrigger value="updates">Updates</TabsTrigger>
              </TabsList>

              <TabsContent value="brief" className="mt-4">
                <ChallengeBriefSections 
                  challenge={challenge} 
                  isTeamMember={isAnyTeamMember}
                  isAdmin={isAdmin}
                  canEdit={canEdit}
                  editModeActive={editModeActive}
                  onUpdate={handleUpdate}
                />
              </TabsContent>

              <TabsContent value="tasks" className="mt-4">
                <TaskList
                  challengeId={challenge.id}
                  challengeTitle={challenge.title}
                  problemStatement={challenge.problem_description}
                  canManage={isTeamLead || isAdmin || isClaimant}
                  isTeamMember={isTeamMember || isClaimant}
                />
              </TabsContent>

              <TabsContent value="team" className="mt-4">
                <TeamMembersList
                  challengeId={challenge.id}
                  teamId={teamInfo?.id}
                  teamName={teamInfo?.name}
                  isTeamLead={isTeamLead}
                  onUpdate={handleUpdate}
                  onTeamDeleted={() => {
                    handleUpdate();
                    onOpenChange(false);
                  }}
                />
              </TabsContent>

              <TabsContent value="updates" className="mt-4">
                <ChallengeUpdatesTimeline
                  challengeId={challenge.id}
                  canPost={isTeamMember || isClaimant}
                />
              </TabsContent>
            </Tabs>
          </>
        ) : (
          <div className="text-center py-8 text-muted-foreground">
            Challenge not found
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default ChallengeModal;
